import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AI_GRAY_50, AI_GRAY_200} from 'llm/chat-library/ui/AIStyleConstants';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import {assertNever} from 'util/Types';

const AI_CHAT_SURFACE_RADIUS = '16px';
const AI_CHAT_SURFACE_BORDER = `1px solid ${AI_GRAY_200}`;
const AI_CHAT_USER_MESSAGE_BACKGROUND = AI_GRAY_50;

export function ChatMessageToolbar({
  left,
  right,
  hideRightUntilHover = false,
  showRightAction = true,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
  hideRightUntilHover?: boolean;
  showRightAction?: boolean;
}): JSX.Element {
  const rightActionTestProps = hideRightUntilHover
    ? getTestProps({component: 'ChatMessageToolbar', element: 'hover-action-container'})
    : {};

  return (
    <div className={css(styles.messageToolbar)}>
      {left}
      <div
        className={css(hideRightUntilHover && !showRightAction && styles.messageToolbarRightHiddenUntilHover)}
        data-chat-message-toolbar-hover-action={hideRightUntilHover}
        {...rightActionTestProps}
      >
        {right}
      </div>
    </div>
  );
}

export const ChatMessageBubble = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    toolbar: React.ReactNode;
    faded?: boolean;
    variant: 'report' | 'reply' | 'user_message';
    /** Optional system element rendered inside the message border, below the body/toolbar. */
    footer?: React.ReactNode;
  }
>(({children, variant, toolbar, faded = false, footer = null}, ref): JSX.Element => {
  const body = (
    <>
      {children}
      {toolbar}
      {footer}
    </>
  );
  switch (variant) {
    case 'report':
      return (
        <div
          ref={ref}
          className={css(styles.systemReportLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'assistant-message-bubble'})}
        >
          <div
            className={css(styles.systemReportContainer)}
            {...getTestProps({component: 'ChatMessageBubble', element: 'system-report-message'})}
          >
            {body}
          </div>
        </div>
      );
    case 'reply':
      return (
        <div
          ref={ref}
          className={css(styles.assistantMessageLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'assistant-message-bubble'})}
        >
          <div className={css(styles.assistantMessageContainer)}>
            <div className={css(styles.assistantMessageContent)}>{body}</div>
          </div>
        </div>
      );
    case 'user_message':
      return (
        <div
          ref={ref}
          className={css(styles.userMessageLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'user-message-bubble'})}
        >
          <div
            className={css(styles.userMessageContainer, faded && styles.userMessageContainerFaded)}
            {...getTestProps({
              component: 'ChatMessageBubble',
              element: faded ? 'user-message-bubble-faded' : 'user-message-bubble-solid',
            })}
          >
            {body}
          </div>
        </div>
      );
    default:
      throw new Error(`Unknown variant: ${assertNever(variant)}`);
  }
});

const styles = StyleSheet.create({
  assistantMessageLayout: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  assistantMessageContainer: {
    border: AI_CHAT_SURFACE_BORDER,
    borderRadius: AI_CHAT_SURFACE_RADIUS,
    padding: '12px 20px 20px',
    maxWidth: '80%',
    ':hover [data-chat-message-toolbar-hover-action="true"]': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    ':focus-within [data-chat-message-toolbar-hover-action="true"]': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  assistantMessageContent: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },
  userMessageLayout: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userMessageContainer: {
    ...FONT_PROXIMA_14,
    padding: '12px 20px 12px 16px',
    maxWidth: 'min(400px, 80%)',
    backgroundColor: AI_CHAT_USER_MESSAGE_BACKGROUND,
    whiteSpace: 'pre-wrap',
    borderRadius: AI_CHAT_SURFACE_RADIUS,
    overflowWrap: 'break-word',
    lineHeight: 1.5,
    fontSize: 14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  userMessageContainerFaded: {
    opacity: 0.5,
  },
  systemReportLayout: {
    display: 'flex',
    flexDirection: 'column',
  },
  systemReportContainer: {
    ...FONT_PROXIMA_14,
    border: AI_CHAT_SURFACE_BORDER,
    borderRadius: AI_CHAT_SURFACE_RADIUS,
    padding: '20px',
    lineHeight: 1.5,
    fontSize: 14,
  },
  messageToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  messageToolbarRightHiddenUntilHover: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease-in-out',
  },
});
